import {useMemo, type ReactNode} from 'react'
import type {PendingMessage} from '@microsoft/agent-host-protocol'
import type {ProvisioningStatus} from '@github-ui/ahp-relay/session-host/resolve-project'
import type {ModelForTokenUsage} from '@github-ui/copilot-chat/components/MessageTokenUsage'
import {resolveTimelineView, type TimelineView} from '@github-ui/ahp-relay/ahp-session-selectors'
import {
  useEstablishedAhpSessionViewState as useEstablishedAhpSessionSnapshot,
  useOptionalEstablishedAhpSessionViewState,
  type EstablishedAhpSessionViewState,
} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session-view'
import {useOptionalEstablishedAhpSession} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session'
import type {TaskStartup} from '../../../ahp/task-startup-store'
import type {ContributedInstructionsMetadata} from '@github-ui/ahp-session-client/contracts'
import {readCheckpoints, type CheckpointAnchors, type RelayCheckpoint} from '@github-ui/ahp-relay/session-checkpoints'
import type {AhpSessionBundle} from '../../../utils/replay-ahp-events'

import {AhpTimelineView, type TimelineMarker} from './AhpTimelineView'
import {AhpSavedTimeline} from './AhpSessionHistoryTimeline'
import {toCompactionTimelineMarkers} from './CompactionStatusEntry'
import {compactionAnchorTurns, projectCompactionMarkers} from './compaction-marker'
import {useCompactionAnnouncements} from './use-compaction-announcements'
import {useCompactionCheckpointAnchors} from './use-compaction-checkpoint-anchors'

export interface EstablishedAhpSessionTimelineProps {
  ariaLabel?: string
  additionalErrorMessage?: string
  timelineMarkers?: TimelineMarker[]
  availableModels?: ModelForTokenUsage[]
  suppressInitialUserMessage?: boolean
  suppressQueuedMessages?: boolean
  onCancelQueuedMessage?: (id: string) => void | Promise<void>
  onSendNowQueuedMessage?: (message: PendingMessage) => void | Promise<void>
  fallback?: ReactNode
  wakeable?: boolean
  taskStartup?: TaskStartup
  onContributedInstructions?: (instructions: ContributedInstructionsMetadata, trigger: HTMLElement) => void
  taskStartupCloneProgress?: number
  fallbackProvisioning?: ProvisioningStatus
  historyBundle?: AhpSessionBundle
  preserveHistory?: boolean
  loadingPresentation?: 'local' | 'page'
}

/**
 * Provider-backed root timeline. The relay lease and root subscription belong
 * to the established session; this component only projects its canonical view
 * into the existing pure AHP timeline renderer.
 */
export function EstablishedAhpSessionTimeline(props: EstablishedAhpSessionTimelineProps) {
  const timeline = useEstablishedAhpSessionSnapshot()
  return renderEstablishedAhpSessionTimeline(props, timeline)
}

export function OptionalEstablishedAhpSessionTimeline({
  fallbackView,
  fallbackErrorMessage,
  ...props
}: EstablishedAhpSessionTimelineProps & {
  fallbackView: TimelineView
  fallbackErrorMessage?: string
}) {
  const session = useOptionalEstablishedAhpSession()
  const timeline = useOptionalEstablishedAhpSessionViewState()
  const checkpoints = useMemo(() => readCheckpoints(timeline.sessionState?._meta), [timeline.sessionState])
  const checkpointAnchors = useCompactionCheckpointAnchors(props.historyBundle)
  useCompactionAnnouncements(session?.identity.sessionId, checkpoints)
  return renderEstablishedAhpSessionTimeline(
    props,
    timeline,
    session ? undefined : {view: fallbackView, errorMessage: fallbackErrorMessage},
    checkpoints,
    checkpointAnchors,
  )
}

function renderEstablishedAhpSessionTimeline(
  {
    ariaLabel,
    additionalErrorMessage,
    timelineMarkers,
    availableModels,
    suppressInitialUserMessage,
    suppressQueuedMessages,
    onCancelQueuedMessage,
    onSendNowQueuedMessage,
    fallback,
    wakeable,
    taskStartup,
    onContributedInstructions,
    taskStartupCloneProgress,
    fallbackProvisioning,
    historyBundle,
    preserveHistory,
    loadingPresentation,
  }: EstablishedAhpSessionTimelineProps,
  timeline: EstablishedAhpSessionViewState,
  disconnected?: {view: TimelineView; errorMessage?: string},
  compactionCheckpoints?: readonly RelayCheckpoint[],
  checkpointAnchors?: CheckpointAnchors,
) {
  const state = timeline.state
  const terminal = timeline.status === 'error' || timeline.status === 'closed'
  const canMutateQueue = timeline.passiveKnown && !timeline.passive
  const errorMessage = disconnected?.errorMessage ?? (historyBundle ? undefined : timeline.error)
  const modelFailureMessages = timeline.modelFailures
    ? Object.fromEntries(
        Object.entries(timeline.modelFailures).map(([turnId, failure]) => [
          turnId,
          failure.model
            ? `${failure.model} could not run: ${failure.message}`
            : `The model could not run: ${failure.message}`,
        ]),
      )
    : undefined

  if (!state && !historyBundle && terminal && fallback !== undefined && !taskStartup) return <>{fallback}</>
  const Timeline = preserveHistory || historyBundle ? AhpSavedTimeline : AhpTimelineView
  const compactionMarkers = toCompactionTimelineMarkers(
    projectCompactionMarkers(compactionCheckpoints, compactionAnchorTurns(state, historyBundle), checkpointAnchors),
  )
  const markers = compactionMarkers.length > 0 ? [...(timelineMarkers ?? []), ...compactionMarkers] : timelineMarkers

  return (
    <Timeline
      bundle={historyBundle}
      state={state}
      view={disconnected?.view ?? resolveTimelineView(!!state, timeline.status, timeline.error)}
      errorMessage={errorMessage}
      additionalErrorMessage={additionalErrorMessage}
      ariaLabel={ariaLabel}
      taskStartup={taskStartup}
      taskStartupCloneProgress={taskStartupCloneProgress}
      provisioning={disconnected ? fallbackProvisioning : timeline.provisioning}
      {...(markers ? {timelineMarkers: markers} : {})}
      steeringMessages={timeline.steeringMessages}
      {...(suppressInitialUserMessage ? {suppressInitialUserMessage} : {})}
      {...(suppressQueuedMessages ? {suppressQueuedMessages} : {})}
      {...(canMutateQueue && onCancelQueuedMessage ? {onCancelQueuedMessage} : {})}
      {...(canMutateQueue && onSendNowQueuedMessage ? {onSendNowQueuedMessage} : {})}
      {...(modelFailureMessages ? {modelFailureMessages} : {})}
      {...(!disconnected && timeline.provisioning?.phase === 'cloning'
        ? {provisioningMessage: 'Cloning repository…'}
        : !disconnected && wakeable && !timeline.state && !terminal
          ? {provisioningMessage: 'Waking compute…'}
          : {})}
      {...(!disconnected && wakeable && !timeline.state && !terminal
        ? {provisioningAnnouncement: 'Waking compute…'}
        : {})}
      availableModels={availableModels}
      onContributedInstructions={onContributedInstructions}
      loadingPresentation={loadingPresentation}
      loadingSourceKind={disconnected?.view === 'loading' && preserveHistory ? 'history' : 'connection'}
    />
  )
}
